import { useState, useEffect, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { dpsScheduleResultAtom } from './atoms';
import type { ScheduledSlotDates } from './types';

/**
 * Fetches the scheduled_slots.json blob via the SAS URL returned from the
 * DPS schedule endpoint, then indexes results by unique_slot_id.
 *
 * Returns an empty map when no DPS is selected or the URL is unavailable.
 *
 * @param {number} packageId - The package ID (atom family key).
 * @return {Record<string, ScheduledSlotDates>} Scheduled dates keyed by slot ID.
 */
export const useScheduledDates = (
  packageId: number
): Record<string, ScheduledSlotDates> => {
  const dpsScheduleResult = useAtomValue(dpsScheduleResultAtom(packageId));
  const sasUrl = dpsScheduleResult?.scheduledSlotsSasUrl ?? null;

  const [dateMap, setDateMap] = useState<Record<string, ScheduledSlotDates>>(
    {}
  );

  useEffect(() => {
    if (!sasUrl) {
      setDateMap({});
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(sasUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const json = await response.json();
        const data = json.scheduled_slots ?? json;

        if (cancelled) return;

        if (Array.isArray(data)) {
          // New format: array of slot objects
          const map: Record<string, ScheduledSlotDates> = {};
          for (const entry of data) {
            map[entry.unique_slot_id] = entry;
          }
          setDateMap(map);
        } else if (data && typeof data === 'object') {
          // Old format: already keyed by slot ID
          setDateMap(data as Record<string, ScheduledSlotDates>);
        } else {
          setDateMap({});
        }
      } catch {
        if (!cancelled) setDateMap({});
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sasUrl]);

  return useMemo(() => dateMap, [dateMap]);
};
